import { Grid, Button, Divider } from '@material-ui/core'
import React, { useEffect, useState } from 'react'

import { ReactComponent as ResetIcon } from 'assets/images/reset-icon.svg'
import { COLORS } from 'styles'

import { ColorPicker } from '../ColorPicker'
import { ImageUploader } from '../ImageUploader'
import { useStyles } from '../styles'
import { Style } from '../types'

interface BackofficeProps {
  style: Style
  setStyle: (style: Style) => void
  setLogoFile: (file: File | undefined) => void
  onSave: () => void
}

const Backoffice: React.FC<BackofficeProps> = ({ style, setStyle, setLogoFile, onSave }) => {
  const classes = useStyles()
  const [isResetting, setIsResetting] = useState(false)

  const [primaryColor, setPrimaryColor] = useState<string>(style.colors.PRIMARY_MAIN)
  const [primaryContrastColor, setPrimaryContrastColor] = useState<string>(
    style.colors.PRIMARY_CONTRAST,
  )
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [secondaryColor, setSecondaryColor] = useState<string>(style.colors.SECONDARY_MAIN)
  const [backgroundColor, setBackgroundColor] = useState<string>(style.colors.BACKGROUND)
  const [logoPreview, setLogoPreview] = useState<string>(style.logoUrl)

  useEffect(() => {
    if (isResetting) {
      onSave()
      setIsResetting(false)
    }
  }, [style, isResetting, onSave])

  useEffect(() => {
    setPrimaryColor(style.colors.PRIMARY_MAIN)
    setPrimaryContrastColor(style.colors.PRIMARY_CONTRAST)
    setSecondaryColor(style.colors.SECONDARY_MAIN)
    setBackgroundColor(style.colors.BACKGROUND)
    setLogoPreview(style.logoUrl)
  }, [style])

  const handlePrimaryColorChange = (color: string) => {
    setPrimaryColor(color)
    setStyle({
      ...style,
      colors: {
        ...style.colors,
        PRIMARY_MAIN: color,
      },
    })
  }

  const handlePrimaryContrastColorChange = (color: string) => {
    setPrimaryContrastColor(color)
    setStyle({
      ...style,
      colors: {
        ...style.colors,
        PRIMARY_CONTRAST: color,
      },
    })
  }

  const handleBackgroundChange = (color: string) => {
    setBackgroundColor(color)
    setStyle({
      ...style,
      colors: {
        ...style.colors,
        BACKGROUND: color,
      },
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSecondaryColorChange = (color: string) => {
    setSecondaryColor(color)
    setStyle({
      ...style,
      colors: {
        ...style.colors,
        SECONDARY_MAIN: color,
      },
    })
  }

  const handleLogoChange = (file: File) => {
    const previewUrl = URL.createObjectURL(file)
    setLogoPreview(previewUrl)
    setLogoFile(file)
    setStyle({
      ...style,
      logoUrl: previewUrl,
    })
  }

  const handleLogoDelete = () => {
    setLogoPreview('')
    setLogoFile(undefined)
    setStyle({
      ...style,
      logoUrl: '',
    })
  }

  const reset = () => {
    // Primero actualizamos los estados locales
    setPrimaryColor(COLORS.BASE)
    setPrimaryContrastColor(COLORS.ACCENT)
    setSecondaryColor(COLORS.SECONDARY_MAIN)
    setBackgroundColor(COLORS.PRIMARY_BACKGROUND)
    setLogoPreview('')
    setLogoFile(undefined)

    // Luego actualizamos el estado del padre
    const newStyle = {
      ...style,
      colors: {
        PRIMARY_MAIN: COLORS.BASE,
        PRIMARY_CONTRAST: COLORS.ACCENT,
        SECONDARY_MAIN: COLORS.SECONDARY_MAIN,
        BACKGROUND: COLORS.PRIMARY_BACKGROUND,
      },
      logoUrl: '',
    }

    setStyle(newStyle)
    setIsResetting(true)
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Button
          onClick={reset}
          variant="outlined"
          color="primary"
          className={classes.resetButton}
          startIcon={<ResetIcon className={classes.resetIcon} />}
        >
          Restaurar valores por defecto
        </Button>
      </Grid>
      <Grid item xs={12}>
        <Divider className={classes.divider} />
      </Grid>
      <Grid item xs={12}>
        <div className={classes.dividerContainer}>
          <div className={classes.dividerText}>Colores</div>
        </div>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <ColorPicker
          label="Banner y botones"
          color={primaryColor}
          handler={handlePrimaryColorChange}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <ColorPicker
          label="Fuentes"
          color={primaryContrastColor}
          handler={handlePrimaryContrastColorChange}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <ColorPicker label="Fondos" color={backgroundColor} handler={handleBackgroundChange} />
      </Grid>
      <Grid item xs={12}>
        <Divider className={classes.divider} />
      </Grid>
      <Grid item xs={12}>
        <div className={classes.dividerContainer}>
          <div className={classes.dividerText}>Logo</div>
        </div>
      </Grid>
      <Grid item xs={12}>
        <ImageUploader
          label="Logo"
          preview={logoPreview}
          onChange={handleLogoChange}
          onDelete={handleLogoDelete}
        />
      </Grid>
    </Grid>
  )
}

export { Backoffice }
